import { useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useUser } from '@clerk/react';
import { Bug, X, Paperclip, CheckCircle2 } from 'lucide-react';
import { useClerkReady } from '../lib/useClerkReady';
import { useAuthedApi } from '../lib/useAuthedApi';
import { uploadFile } from '../lib/uploader';

/**
 * Floating "report a problem" widget for signed-in dashboard users. Captures
 * the message plus an optional screenshot and a bundle of debug context (page
 * URL, who's logged in, time, browser) and POSTs to /feedback, which emails it
 * to the maintainer. Onboarding users double as QA — this is their easy button.
 *
 * Self-gating: renders nothing unless signed in and on an /admin or /staff
 * page, so it never shows on public booking pages or to logged-out visitors.
 */
export default function FeedbackWidget() {
  const { isSignedIn, orgRole } = useClerkReady();
  const { pathname } = useLocation();
  const { user } = useUser();
  const apiFn = useAuthedApi();
  const fileRef = useRef<HTMLInputElement>(null);

  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const inDashboard = pathname.startsWith('/admin') || pathname.startsWith('/staff');
  if (!isSignedIn || !inDashboard) return null;

  function reset() {
    setMessage('');
    setFile(null);
    setErr(null);
    setSent(false);
    if (fileRef.current) fileRef.current.value = '';
  }

  function onPaste(e: React.ClipboardEvent) {
    const img = Array.from(e.clipboardData.items).find((i) => i.type.startsWith('image/'));
    const f = img?.getAsFile();
    if (f) setFile(f);
  }

  async function submit() {
    if (!message.trim()) return;
    setSending(true);
    setErr(null);
    try {
      let screenshotUrl: string | null = null;
      if (file) {
        try {
          const up = await uploadFile(file);
          screenshotUrl = up.url;
        } catch {
          // Non-fatal — send the report without the screenshot rather than lose it.
        }
      }
      await apiFn('/feedback', {
        method: 'POST',
        body: {
          message: message.trim(),
          screenshot_url: screenshotUrl,
          context: {
            url: window.location.href,
            user_agent: navigator.userAgent,
            viewport: `${window.innerWidth}x${window.innerHeight}`,
            screen: `${window.screen.width}x${window.screen.height}`,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            local_time: new Date().toString(),
            user_name: user?.fullName ?? undefined,
            user_email: user?.primaryEmailAddress?.emailAddress ?? undefined,
            role: orgRole ?? undefined,
          },
        },
      });
      setSent(true);
      setMessage('');
      setFile(null);
      if (fileRef.current) fileRef.current.value = '';
      setTimeout(() => {
        setOpen(false);
        setSent(false);
      }, 1600);
    } catch (e) {
      setErr((e as Error).message || 'Could not send. Please try again.');
    } finally {
      setSending(false);
    }
  }

  return (
    <>
      {open && (
        <div className="feedback-panel" role="dialog" aria-label="Report a problem">
          <div className="feedback-head">
            <h3>Report a problem</h3>
            <button
              className="ghost feedback-close"
              onClick={() => {
                setOpen(false);
                reset();
              }}
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>
          {sent ? (
            <p className="feedback-sent">
              <CheckCircle2 size={16} /> Thanks — sent! We&rsquo;ll take a look.
            </p>
          ) : (
            <>
              <p className="muted small" style={{ marginTop: 0 }}>
                Found a bug or something confusing? Tell us what happened.
              </p>
              <textarea
                rows={4}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onPaste={onPaste}
                placeholder="What went wrong? What were you trying to do?"
                maxLength={5000}
                autoFocus
              />
              <div className="feedback-attach">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                />
                {file ? (
                  <span className="feedback-file">
                    <Paperclip size={13} /> {file.name}
                    <button
                      className="ghost feedback-file-x"
                      onClick={() => {
                        setFile(null);
                        if (fileRef.current) fileRef.current.value = '';
                      }}
                      aria-label="Remove screenshot"
                    >
                      <X size={12} />
                    </button>
                  </span>
                ) : (
                  <button className="ghost small" type="button" onClick={() => fileRef.current?.click()}>
                    <Paperclip size={13} /> Attach a screenshot
                  </button>
                )}
              </div>
              {err && <p className="err small">{err}</p>}
              <button
                className="primary"
                onClick={submit}
                disabled={sending || !message.trim()}
                style={{ marginTop: '0.6rem', width: '100%' }}
              >
                {sending ? 'Sending…' : 'Send report'}
              </button>
            </>
          )}
        </div>
      )}
      <button
        className="feedback-fab"
        onClick={() => setOpen((o) => !o)}
        aria-label="Report a problem"
        title="Report a problem"
      >
        <Bug size={20} />
      </button>
    </>
  );
}
